import { supabase } from '../supabaseClient'

// Utility function to fetch all records with pagination to remove 1000 record limit
const fetchAllRecords = async (queryOrFn, batchSize = 1000) => {
  let allData = []
  let start = 0
  let hasMore = true

  while (hasMore) {
    const q = typeof queryOrFn === 'function' ? queryOrFn() : queryOrFn
    const { data, error } = await q.range(start, start + batchSize - 1)
    
    if (error) {
      throw error
    }
    
    if (data && data.length > 0) {
      allData = allData.concat(data)
      if (data.length < batchSize) {
        hasMore = false
      } else {
        start += batchSize
      }
    } else {
      hasMore = false
    }
  }
  
  return allData
}

const BARANGAY_NAME_MAP = {
  'Poblacion 1': 'Poblacion 1 - (Punta)',
  'Poblacion-1': 'Poblacion 1 - (Punta)',
  'Poblacion1': 'Poblacion 1 - (Punta)',
  'Poblacion 1 (Punta)': 'Poblacion 1 - (Punta)',
  'Poblacion-1 (Punta)': 'Poblacion 1 - (Punta)',
  'Poblacion 1 - (Punta)': 'Poblacion 1 - (Punta)',
  'Poblacion-1 - (Punta)': 'Poblacion 1 - (Punta)',

  'Poblacion 2': 'Poblacion 2 - (Tinago)',
  'Poblacion-2': 'Poblacion 2 - (Tinago)',
  'Poblacion2': 'Poblacion 2 - (Tinago)',
  'Poblacion 2 (Tinago)': 'Poblacion 2 - (Tinago)',
  'Poblacion-2 (Tinago)': 'Poblacion 2 - (Tinago)',
  'Poblacion 2 - (Tinago)': 'Poblacion 2 - (Tinago)',
  'Poblacion-2 - (Tinago)': 'Poblacion 2 - (Tinago)',

  'Poblacion 3': 'Poblacion 3 - (Suyor)',
  'Poblacion-3': 'Poblacion 3 - (Suyor)',
  'Poblacion3': 'Poblacion 3 - (Suyor)',
  'Poblacion 3 (Suyor)': 'Poblacion 3 - (Suyor)',
  'Poblacion-3 (Suyor)': 'Poblacion 3 - (Suyor)',
  'Poblacion 3 - (Suyor)': 'Poblacion 3 - (Suyor)',
  'Poblacion-3 - (Suyor)': 'Poblacion 3 - (Suyor)',

  'Poblacion 4': 'Poblacion 4 - (Tabok)',
  'Poblacion-4': 'Poblacion 4 - (Tabok)',
  'Poblacion4': 'Poblacion 4 - (Tabok)',
  'Poblacion 4 (Tabok)': 'Poblacion 4 - (Tabok)',
  'Poblacion-4 (Tabok)': 'Poblacion 4 - (Tabok)',
  'Poblacion 4 - (Tabok)': 'Poblacion 4 - (Tabok)',
  'Poblacion-4 - (Tabok)': 'Poblacion 4 - (Tabok)',

  'Poblacion 5': 'Poblacion 5 - (Hamtik)',
  'Poblacion-5': 'Poblacion 5 - (Hamtik)',
  'Poblacion5': 'Poblacion 5 - (Hamtik)',
  'Poblacion 5 (Hamtik)': 'Poblacion 5 - (Hamtik)',
  'Poblacion-5 (Hamtik)': 'Poblacion 5 - (Hamtik)',
  'Poblacion 5 - (Hamtik)': 'Poblacion 5 - (Hamtik)',
  'Poblacion-5 - (Hamtik)': 'Poblacion 5 - (Hamtik)',
}

export const formatBarangayName = (name) => {
  if (!name) return name
  const trimmed = name.trim()
  return BARANGAY_NAME_MAP[trimmed] || name
}

export const reportService = {
  // Get unique barangays for filters
  async getBarangays() {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('barangay')
        .not('barangay', 'is', null)
        .limit(10000)

      if (error) throw error

      const unique = [...new Set(data.map(c => formatBarangayName(c.barangay)))]
      return { success: true, data: unique.sort() }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },

  // Get unique billed months for filters
  async getBilledMonths() {
    try {
      const baseQuery = supabase
        .from('bills')
        .select('billedmonth')
        .not('billedmonth', 'is', null)

      const data = await fetchAllRecords(baseQuery)
      const unique = [...new Set(data.map(b => b.billedmonth).filter(Boolean))]
      return { success: true, data: unique.sort().reverse() }
    } catch (error) {
      console.error('Error getting billed months:', error)
      return { success: false, error: error.message, data: [] }
    }
  },
  // Get customers with unusual usage across all billing history
  async getUsageAnomalies(threshold = 10, paymentStatus = 'All') {
    try {
      let baseQuery = supabase
        .from('bills')
        .select('customerid, billedmonth, consumption, paymentstatus')
        .order('customerid', { ascending: true })
        .order('billedmonth', { ascending: true })

      if (paymentStatus === 'Paid' || paymentStatus === 'Unpaid') {
        baseQuery = baseQuery.eq('paymentstatus', paymentStatus)
      }

      let allBills = []
      let start = 0
      const batchSize = 1000
      let hasMore = true

      while (hasMore) {
        const { data: batch, error } = await baseQuery.range(start, start + batchSize - 1)
        if (error) throw error

        if (batch && batch.length > 0) {
          allBills = allBills.concat(batch)
          if (batch.length < batchSize) {
            hasMore = false
          } else {
            start += batchSize
          }
        } else {
          hasMore = false
        }
      }

      if (allBills.length === 0) {
        return { success: true, data: [] }
      }

      const byCustomer = {}

      allBills.forEach(bill => {
        const customerid = bill.customerid
        if (!customerid) return

        if (!byCustomer[customerid]) {
          byCustomer[customerid] = []
        }

        const cons = parseFloat(bill.consumption || 0)
        byCustomer[customerid].push({
          month: bill.billedmonth,
          consumption: cons
        })
      })

      const anomalies = []

      Object.entries(byCustomer).forEach(([customerid, bills]) => {
        if (!bills || bills.length < 2) {
          return
        }

        const total = bills.reduce((sum, b) => sum + b.consumption, 0)
        const count = bills.length

        bills.forEach(bill => {
          const historyCount = count - 1
          if (historyCount <= 0) return

          const historyTotal = total - bill.consumption
          const avgHistory = historyTotal / historyCount
          const diff = bill.consumption - avgHistory

          if (diff >= threshold) {
            anomalies.push({
              customerid,
              avgHistory,
              current: bill.consumption,
              diff,
              month: bill.month
            })
          }
        })
      })

      if (anomalies.length === 0) {
        return { success: true, data: [] }
      }

      // Keep only the strongest anomaly (largest diff) per customer
      const bestByCustomer = {}
      anomalies.forEach(a => {
        const existing = bestByCustomer[a.customerid]
        if (!existing || a.diff > existing.diff) {
          bestByCustomer[a.customerid] = a
        }
      })

      const bestAnomalies = Object.values(bestByCustomer)

      const customerIds = bestAnomalies.map(a => a.customerid)

      const { data: customers, error: customerError } = await supabase
        .from('customers')
        .select('customerid, name, address, barangay, type, status')
        .in('customerid', customerIds)

      if (customerError) throw customerError

      const customerMap = {}
      if (customers) {
        customers.forEach(c => {
          customerMap[c.customerid] = c
        })
      }

      const result = bestAnomalies.map(a => {
        const customer = customerMap[a.customerid] || {}
        return {
          customerid: a.customerid,
          name: customer.name || 'Unknown',
          address: customer.address || '',
          barangay: customer.barangay || '',
          type: customer.type || '',
          status: customer.status || '',
          month: a.month,
          currentConsumption: a.current,
          averageHistoryConsumption: a.avgHistory,
          difference: a.diff
        }
      })

      return {
        success: true,
        data: result.sort((a, b) => b.difference - a.difference)
      }
    } catch (error) {
      console.error('Error getting usage anomalies:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // Get unique customer types
  async getCustomerTypes() {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('type')
        .not('type', 'is', null)
        .limit(10000)

      if (error) throw error

      const unique = [...new Set(data.map(c => c.type))]
      return { success: true, data: unique.sort() }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },

  // Aging Report - Accounts Receivable by age
  async getAgingReport(filters = {}, months = null) {
    try {
      const baseQuery = supabase
        .from('bills')
        .select('billid, customerid, billedmonth, totalbillamount')
        .eq('paymentstatus', 'Unpaid')

      const data = await fetchAllRecords(baseQuery)

      // Fetch customer details to get barangay, type, status
      const customerIds = [...new Set(data.map(b => b.customerid).filter(id => id))]
      let customerMap = {}
      if (customerIds.length > 0) {
        const baseQueryCustomers = supabase
          .from('customers')
          .select('customerid, name, address, barangay, type, status')
          .in('customerid', customerIds)
        
        const customers = await fetchAllRecords(baseQueryCustomers)
        if (customers) {
          customers.forEach(c => {
            customerMap[c.customerid] = c
          })
        }
      }

      // Merge customer info into data
      const mergedData = data.map(bill => {
        const customer = customerMap[bill.customerid] || {}
        return {
          ...bill,
          barangay: customer.barangay || 'Unknown',
          type: customer.type || 'Unknown',
          status: customer.status || 'Unknown'
        }
      })

      // Apply filters if provided
      let filteredData = mergedData
      if (filters.barangay) {
        if (Array.isArray(filters.barangay)) {
          if (filters.barangay.length > 0 && !filters.barangay.includes('all')) {
            filteredData = filteredData.filter(b => filters.barangay.includes(b.barangay))
          }
        } else if (filters.barangay !== 'all') {
          filteredData = filteredData.filter(b => b.barangay === filters.barangay)
        }
      }
      if (filters.type) {
        if (Array.isArray(filters.type)) {
          if (filters.type.length > 0 && !filters.type.includes('all')) {
            filteredData = filteredData.filter(b => filters.type.includes(b.type))
          }
        } else if (filters.type !== 'all') {
          filteredData = filteredData.filter(b => b.type === filters.type)
        }
      }
      if (filters.status && filters.status !== 'all') {
        filteredData = filteredData.filter(b => b.status === filters.status)
      }
      if (months && months.length > 0) {
        filteredData = filteredData.filter(b => months.includes(b.billedmonth))
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const aging = {
        current: [],      // 0-30 days
        days_31_60: [],   // 31-60 days
        days_61_90: [],   // 61-90 days
        days_91_120: [],  // 91-120 days
        over_120: []      // Over 120 days
      }

      filteredData.forEach(bill => {
        const billDate = new Date(bill.billedmonth)
        const daysDiff = Math.floor((today - billDate) / (1000 * 60 * 60 * 24))

        if (daysDiff <= 30) aging.current.push(bill)
        else if (daysDiff <= 60) aging.days_31_60.push(bill)
        else if (daysDiff <= 90) aging.days_61_90.push(bill)
        else if (daysDiff <= 120) aging.days_91_120.push(bill)
        else aging.over_120.push(bill)
      })

      const summary = {
        current: {
          count: aging.current.length,
          amount: aging.current.reduce((sum, b) => sum + parseFloat(b.totalbillamount || 0), 0)
        },
        days_31_60: {
          count: aging.days_31_60.length,
          amount: aging.days_31_60.reduce((sum, b) => sum + parseFloat(b.totalbillamount || 0), 0)
        },
        days_61_90: {
          count: aging.days_61_90.length,
          amount: aging.days_61_90.reduce((sum, b) => sum + parseFloat(b.totalbillamount || 0), 0)
        },
        days_91_120: {
          count: aging.days_91_120.length,
          amount: aging.days_91_120.reduce((sum, b) => sum + parseFloat(b.totalbillamount || 0), 0)
        },
        over_120: {
          count: aging.over_120.length,
          amount: aging.over_120.reduce((sum, b) => sum + parseFloat(b.totalbillamount || 0), 0)
        }
      }

      const totalAmount = Object.values(summary).reduce((sum, s) => sum + s.amount, 0)
      const totalCount = Object.values(summary).reduce((sum, s) => sum + s.count, 0)

      return {
        success: true,
        data: {
          summary,
          totalAmount,
          totalCount,
          details: aging
        }
      }
    } catch (error) {
      console.error('Error getting aging report:', error)
      return { success: false, error: error.message }
    }
  },

  // Customer Analysis Report
  async getCustomerAnalysis(filters = {}) {
    try {
      const baseQuery = supabase
        .from('customers')
        .select('*')

      const data = await fetchAllRecords(baseQuery)

      let filteredData = data
      if (filters.barangay) {
        if (Array.isArray(filters.barangay)) {
          if (filters.barangay.length > 0 && !filters.barangay.includes('all')) {
            filteredData = filteredData.filter(c => filters.barangay.includes(c.barangay))
          }
        } else if (filters.barangay !== 'all') {
          filteredData = filteredData.filter(c => c.barangay === filters.barangay)
        }
      }
      if (filters.type) {
        if (Array.isArray(filters.type)) {
          if (filters.type.length > 0 && !filters.type.includes('all')) {
            filteredData = filteredData.filter(c => filters.type.includes(c.type))
          }
        } else if (filters.type !== 'all') {
          filteredData = filteredData.filter(c => c.type === filters.type)
        }
      }
      if (filters.status && filters.status !== 'all') {
        filteredData = filteredData.filter(c => c.status === filters.status)
      }

      // Group by status
      const byStatus = {}
      filteredData.forEach(c => {
        const status = c.status || 'Unknown'
        byStatus[status] = (byStatus[status] || 0) + 1
      })

      // Group by type
      const byType = {}
      filteredData.forEach(c => {
        const type = c.type || 'Unknown'
        byType[type] = (byType[type] || 0) + 1
      })

      // Group by barangay
      const byBarangay = {}
      filteredData.forEach(c => {
        const barangay = c.barangay || 'Unknown'
        byBarangay[barangay] = (byBarangay[barangay] || 0) + 1
      })

      return {
        success: true,
        data: {
          total: filteredData.length,
          active: filteredData.filter(c => c.status === 'Active').length,
          inactive: filteredData.filter(c => c.status === 'Inactive').length,
          byStatus: Object.entries(byStatus).map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count),
          byType: Object.entries(byType).map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count),
          byBarangay: Object.entries(byBarangay).map(([barangay, count]) => ({ barangay, count })).sort((a, b) => b.count - a.count)
        }
      }
    } catch (error) {
      console.error('Error getting customer analysis:', error)
      return { success: false, error: error.message }
    }
  },

  // Get water usage report by billing months
  async getWaterUsageReport(months, filters = {}) {
    try {
      if (!months || months.length === 0) {
        return {
          success: true,
          data: {
            totalConsumption: 0,
            averageConsumption: 0,
            billCount: 0,
            byMonth: [],
            byBarangay: [],
            rawData: []
          }
        }
      }

      const baseQuery = supabase
        .from('bills')
        .select('billedmonth, consumption, customerid')
        .in('billedmonth', months)
        .order('billedmonth', { ascending: true })

      const data = await fetchAllRecords(baseQuery)

      // Fetch customer details to get barangay, type, status
      const customerIds = [...new Set(data.map(b => b.customerid).filter(id => id))]
      let customerMap = {}
      if (customerIds.length > 0) {
        const baseQueryCustomers = supabase
          .from('customers')
          .select('customerid, name, address, barangay, type, status, meterno')
          .in('customerid', customerIds)
        
        const customers = await fetchAllRecords(baseQueryCustomers)
        if (customers) {
          customers.forEach(c => {
            customerMap[c.customerid] = c
          })
        }
      }

      // Merge customer info into data
      const mergedData = data.map(bill => {
        const customer = customerMap[bill.customerid] || {}
        const rawBrgy = customer.barangay || 'Unknown'
        return {
          ...bill,
          barangay: formatBarangayName(rawBrgy),
          rawBarangay: rawBrgy,
          type: customer.type || 'Unknown',
          status: customer.status || 'Unknown',
          customername: customer.name || 'Unknown',
          address: customer.address || '',
          meterno: customer.meterno || ''
        }
      })

      // Apply filters if provided
      let filteredData = mergedData
      if (filters.barangay) {
        if (Array.isArray(filters.barangay)) {
          if (filters.barangay.length > 0 && !filters.barangay.includes('all')) {
            filteredData = filteredData.filter(b => filters.barangay.includes(b.barangay) || filters.barangay.includes(b.rawBarangay))
          }
        } else if (filters.barangay !== 'all') {
          filteredData = filteredData.filter(b => b.barangay === filters.barangay || b.rawBarangay === filters.barangay)
        }
      }
      if (filters.type) {
        if (Array.isArray(filters.type)) {
          if (filters.type.length > 0 && !filters.type.includes('all')) {
            filteredData = filteredData.filter(b => filters.type.includes(b.type))
          }
        } else if (filters.type !== 'all') {
          filteredData = filteredData.filter(b => b.type === filters.type)
        }
      }
      if (filters.status && filters.status !== 'all') {
        filteredData = filteredData.filter(b => b.status === filters.status)
      }

      // Aggregate data
      const totalConsumption = filteredData.reduce((sum, bill) => sum + parseFloat(bill.consumption || 0), 0)
      const averageConsumption = filteredData.length > 0 ? totalConsumption / filteredData.length : 0

      // Group by month
      const byMonth = {}
      filteredData.forEach(bill => {
        const month = bill.billedmonth
        if (!byMonth[month]) {
          byMonth[month] = { month, consumption: 0, count: 0 }
        }
        byMonth[month].consumption += parseFloat(bill.consumption || 0)
        byMonth[month].count++
      })

      // Group by barangay
      const byBarangay = {}
      filteredData.forEach(bill => {
        const barangay = bill.barangay
        const month = bill.billedmonth
        const consumption = parseFloat(bill.consumption || 0)

        if (!byBarangay[barangay]) {
          byBarangay[barangay] = {
            barangay,
            consumption: 0,
            count: 0,
            customerIds: new Set(),
            monthlyConsumption: {}
          }
        }
        byBarangay[barangay].consumption += consumption
        byBarangay[barangay].count++
        if (bill.customerid) {
          byBarangay[barangay].customerIds.add(bill.customerid)
        }

        if (!byBarangay[barangay].monthlyConsumption[month]) {
          byBarangay[barangay].monthlyConsumption[month] = 0
        }
        byBarangay[barangay].monthlyConsumption[month] += consumption
      })

      return {
        success: true,
        data: {
          totalConsumption,
          averageConsumption,
          billCount: filteredData.length,
          byMonth: Object.values(byMonth),
          byBarangay: Object.values(byBarangay).map(item => ({
            barangay: item.barangay,
            consumption: item.consumption,
            count: item.count,
            customerCount: item.customerIds ? item.customerIds.size : 0,
            monthlyConsumption: item.monthlyConsumption
          })).sort((a, b) => a.barangay.localeCompare(b.barangay)),
          rawData: filteredData
        }
      }
    } catch (error) {
      console.error('Error getting water usage report:', error)
      return { success: false, error: error.message }
    }
  },

  // Get revenue report by billing months
  async getRevenueReport(months, filters = {}) {
    try {
      if (!months || months.length === 0) {
        return {
          success: true,
          data: {
            totalBilled: 0,
            totalPaid: 0,
            totalUnpaid: 0,
            totalSurcharge: 0,
            totalDiscount: 0,
            paidCount: 0,
            unpaidCount: 0,
            collectionRate: 0,
            byMonth: [],
            byBarangay: []
          }
        }
      }

      const baseQuery = supabase
        .from('bills')
        .select('billedmonth, totalbillamount, paymentstatus, datepaid, basicamount, surchargeamount, discountamount, customerid')
        .in('billedmonth', months)

      const data = await fetchAllRecords(baseQuery)

      // Fetch customer details to get barangay, type, status
      const customerIds = [...new Set(data.map(b => b.customerid).filter(id => id))]
      let customerMap = {}
      if (customerIds.length > 0) {
        const baseQueryCustomers = supabase
          .from('customers')
          .select('customerid, name, address, barangay, type, status')
          .in('customerid', customerIds)
        
        const customers = await fetchAllRecords(baseQueryCustomers)
        if (customers) {
          customers.forEach(c => {
            customerMap[c.customerid] = c
          })
        }
      }

      // Merge customer info into data
      const mergedData = data.map(bill => {
        const customer = customerMap[bill.customerid] || {}
        return {
          ...bill,
          barangay: customer.barangay || 'Unknown',
          type: customer.type || 'Unknown',
          status: customer.status || 'Unknown'
        }
      })

      // Apply filters if provided
      let filteredData = mergedData
      if (filters.barangay) {
        if (Array.isArray(filters.barangay)) {
          if (filters.barangay.length > 0 && !filters.barangay.includes('all')) {
            filteredData = filteredData.filter(b => filters.barangay.includes(b.barangay))
          }
        } else if (filters.barangay !== 'all') {
          filteredData = filteredData.filter(b => b.barangay === filters.barangay)
        }
      }
      if (filters.type) {
        if (Array.isArray(filters.type)) {
          if (filters.type.length > 0 && !filters.type.includes('all')) {
            filteredData = filteredData.filter(b => filters.type.includes(b.type))
          }
        } else if (filters.type !== 'all') {
          filteredData = filteredData.filter(b => b.type === filters.type)
        }
      }
      if (filters.status && filters.status !== 'all') {
        filteredData = filteredData.filter(b => b.status === filters.status)
      }

      // Calculate totals
      const totalBilled = filteredData.reduce((sum, bill) => sum + parseFloat(bill.totalbillamount || 0), 0)
      const totalPaid = filteredData
        .filter(bill => bill.paymentstatus === 'Paid')
        .reduce((sum, bill) => sum + parseFloat(bill.totalbillamount || 0), 0)
      const totalUnpaid = filteredData
        .filter(bill => bill.paymentstatus === 'Unpaid')
        .reduce((sum, bill) => sum + parseFloat(bill.totalbillamount || 0), 0)
      const totalSurcharge = filteredData.reduce((sum, bill) => sum + parseFloat(bill.surchargeamount || 0), 0)
      const totalDiscount = filteredData.reduce((sum, bill) => sum + parseFloat(bill.discountamount || 0), 0)

      const paidCount = filteredData.filter(bill => bill.paymentstatus === 'Paid').length
      const collectionRate = filteredData.length > 0 ? (paidCount / filteredData.length) * 100 : 0

      // Group by month
      const byMonth = {}
      filteredData.forEach(bill => {
        const month = bill.billedmonth
        if (!byMonth[month]) {
          byMonth[month] = { month, billed: 0, paid: 0, unpaid: 0 }
        }
        byMonth[month].billed += parseFloat(bill.totalbillamount || 0)
        if (bill.paymentstatus === 'Paid') {
          byMonth[month].paid += parseFloat(bill.totalbillamount || 0)
        } else {
          byMonth[month].unpaid += parseFloat(bill.totalbillamount || 0)
        }
      })

      // Group by barangay
      const byBarangay = {}
      filteredData.forEach(bill => {
        const barangay = bill.barangay
        if (!byBarangay[barangay]) {
          byBarangay[barangay] = { barangay, billed: 0, paid: 0, unpaid: 0, billCount: 0 }
        }
        byBarangay[barangay].billed += parseFloat(bill.totalbillamount || 0)
        byBarangay[barangay].billCount++
        if (bill.paymentstatus === 'Paid') {
          byBarangay[barangay].paid += parseFloat(bill.totalbillamount || 0)
        } else {
          byBarangay[barangay].unpaid += parseFloat(bill.totalbillamount || 0)
        }
      })

      return {
        success: true,
        data: {
          totalBilled,
          totalPaid,
          totalUnpaid,
          totalSurcharge,
          totalDiscount,
          paidCount,
          unpaidCount: filteredData.length - paidCount,
          collectionRate,
          byMonth: Object.values(byMonth),
          byBarangay: Object.values(byBarangay).sort((a, b) => b.billed - a.billed)
        }
      }
    } catch (error) {
      console.error('Error getting revenue report:', error)
      return { success: false, error: error.message }
    }
  },

  // Get payment collection report by billing months
  async getCollectionReport(months, filters = {}) {
    try {
      if (!months || months.length === 0) {
        return {
          success: true,
          data: {
            totalCollected: 0,
            collectionCount: 0,
            byDate: [],
            byCollector: []
          }
        }
      }

      const baseQuery = supabase
        .from('bills')
        .select('datepaid, totalbillamount, paidby, customerid, billedmonth')
        .eq('paymentstatus', 'Paid')
        .in('billedmonth', months)

      const data = await fetchAllRecords(baseQuery)

      // Fetch customer details to get barangay, type, status
      const customerIds = [...new Set(data.map(b => b.customerid).filter(id => id))]
      let customerMap = {}
      if (customerIds.length > 0) {
        const baseQueryCustomers = supabase
          .from('customers')
          .select('customerid, name, address, barangay, type, status')
          .in('customerid', customerIds)
        
        const customers = await fetchAllRecords(baseQueryCustomers)
        if (customers) {
          customers.forEach(c => {
            customerMap[c.customerid] = c
          })
        }
      }

      // Merge customer info into data
      const mergedData = data.map(bill => {
        const customer = customerMap[bill.customerid] || {}
        return {
          ...bill,
          barangay: customer.barangay || 'Unknown',
          type: customer.type || 'Unknown',
          status: customer.status || 'Unknown'
        }
      })

      // Apply filters if provided
      let filteredData = mergedData
      if (filters.barangay) {
        if (Array.isArray(filters.barangay)) {
          if (filters.barangay.length > 0 && !filters.barangay.includes('all')) {
            filteredData = filteredData.filter(b => filters.barangay.includes(b.barangay))
          }
        } else if (filters.barangay !== 'all') {
          filteredData = filteredData.filter(b => b.barangay === filters.barangay)
        }
      }
      if (filters.type) {
        if (Array.isArray(filters.type)) {
          if (filters.type.length > 0 && !filters.type.includes('all')) {
            filteredData = filteredData.filter(b => filters.type.includes(b.type))
          }
        } else if (filters.type !== 'all') {
          filteredData = filteredData.filter(b => b.type === filters.type)
        }
      }
      if (filters.status && filters.status !== 'all') {
        filteredData = filteredData.filter(b => b.status === filters.status)
      }

      const totalCollected = filteredData.reduce((sum, bill) => sum + parseFloat(bill.totalbillamount || 0), 0)

      // Group by date
      const byDate = {}
      filteredData.forEach(bill => {
        const date = bill.datepaid ? bill.datepaid.split('T')[0] : 'Unknown'
        if (!byDate[date]) {
          byDate[date] = { date, amount: 0, count: 0 }
        }
        byDate[date].amount += parseFloat(bill.totalbillamount || 0)
        byDate[date].count++
      })

      // Group by collector
      const byCollector = {}
      filteredData.forEach(bill => {
        const collector = bill.paidby || 'Unknown'
        if (!byCollector[collector]) {
          byCollector[collector] = { collector, amount: 0, count: 0 }
        }
        byCollector[collector].amount += parseFloat(bill.totalbillamount || 0)
        byCollector[collector].count++
      })

      return {
        success: true,
        data: {
          totalCollected,
          collectionCount: filteredData.length,
          byDate: Object.values(byDate),
          byCollector: Object.values(byCollector).sort((a, b) => b.amount - a.amount)
        }
      }
    } catch (error) {
      console.error('Error getting collection report:', error)
      return { success: false, error: error.message }
    }
  },

  // Get top consumers by billing months
  async getTopConsumers(months, limit = 10, filters = {}) {
    try {
      if (!months || months.length === 0) {
        return { success: true, data: [] }
      }

      const baseQuery = supabase
        .from('bills')
        .select('customerid, consumption')
        .in('billedmonth', months)

      const data = await fetchAllRecords(baseQuery)

      // Fetch customer details for all customers represented in bills
      const customerIds = [...new Set(data.map(b => b.customerid).filter(id => id))]
      let customerMap = {}
      if (customerIds.length > 0) {
        const baseQueryCustomers = supabase
          .from('customers')
          .select('customerid, name, address, barangay, meterno, type, status')
          .in('customerid', customerIds)

        const customers = await fetchAllRecords(baseQueryCustomers)
        if (customers) {
          customers.forEach(c => {
            customerMap[c.customerid] = c
          })
        }
      }

      // Merge and filter
      const mergedData = data.map(bill => {
        const customer = customerMap[bill.customerid] || {}
        return {
          ...bill,
          barangay: customer.barangay || 'Unknown',
          type: customer.type || 'Unknown',
          status: customer.status || 'Unknown',
          customername: customer.name || 'Unknown',
          address: customer.address || '',
          meterno: customer.meterno || ''
        }
      })

      let filteredData = mergedData
      if (filters.barangay) {
        if (Array.isArray(filters.barangay)) {
          if (filters.barangay.length > 0 && !filters.barangay.includes('all')) {
            filteredData = filteredData.filter(b => filters.barangay.includes(b.barangay))
          }
        } else if (filters.barangay !== 'all') {
          filteredData = filteredData.filter(b => b.barangay === filters.barangay)
        }
      }
      if (filters.type) {
        if (Array.isArray(filters.type)) {
          if (filters.type.length > 0 && !filters.type.includes('all')) {
            filteredData = filteredData.filter(b => filters.type.includes(b.type))
          }
        } else if (filters.type !== 'all') {
          filteredData = filteredData.filter(b => b.type === filters.type)
        }
      }
      if (filters.status && filters.status !== 'all') {
        filteredData = filteredData.filter(b => b.status === filters.status)
      }

      // Aggregate by customer
      const byCustomer = {}
      filteredData.forEach(bill => {
        const customerid = bill.customerid
        if (!byCustomer[customerid]) {
          const customer = customerMap[customerid] || {}
          byCustomer[customerid] = {
            customerid,
            totalConsumption: 0,
            customername: customer.name || 'Unknown',
            address: customer.address || '',
            barangay: customer.barangay || 'Unknown',
            meterno: customer.meterno || '',
            billCount: 0
          }
        }
        byCustomer[customerid].totalConsumption += parseFloat(bill.consumption || 0)
        byCustomer[customerid].billCount++
      })

      // Sort and get top consumers
      const topConsumers = Object.values(byCustomer)
        .sort((a, b) => b.totalConsumption - a.totalConsumption)
        .slice(0, limit)

      return { success: true, data: topConsumers }
    } catch (error) {
      console.error('Error getting top consumers:', error)
      return { success: false, error: error.message }
    }
  }
}
